import { Grid, IconButton } from '@mui/material'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useEffect } from 'react'
import { CustomController } from '../../../common/CustomController'
import AccountDropdown from '../../../common/AccountDropdown'
import OfficeAutoComplete from '../../../offices/OfficeAutocomplete'
import { DeleteOutline } from '@mui/icons-material'

interface JournalEntryLineItemFormProps {
  lineItemIndex: number
  onRemove?: () => void
  showRemove?: boolean
}

type FieldError = {
  message?: string
  value?: { message: string }
}

export const JournalEntryLineItemForm = ({ lineItemIndex, onRemove, showRemove }: JournalEntryLineItemFormProps) => {
  const { control, trigger, setValue } = useFormContext()
  const basePath = `lineItems[${lineItemIndex}]`
  const debitAmount = useWatch({
    control,
    name: `${basePath}.debitAmount`,
    defaultValue: ''
  })

  const creditAmount = useWatch({
    control,
    name: `${basePath}.creditAmount`,
    defaultValue: ''
  })

  const hasDebitValue = debitAmount !== '' && debitAmount !== null && debitAmount !== undefined && Number(debitAmount) !== 0
  const hasCreditValue = creditAmount !== '' && creditAmount !== null && creditAmount !== undefined && Number(creditAmount) !== 0

  // Just to make the opposite field validation realtime
  useEffect(() => {
    if (hasDebitValue) {
      trigger(`${basePath}.creditAmount`)
    }

    // Only trigger when debitAmount changes, avoid unnecessary re-renders
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [debitAmount])

  // Just to make the opposite field validation realtime
  useEffect(() => {
    if (hasCreditValue) {
      trigger(`${basePath}.debitAmount`)
    }

    // Only trigger when creditAmount changes, avoid unnecessary re-renders
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [creditAmount])

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={3}>
        <Controller
          name={`${basePath}.account`}
          control={control}
          render={({ field, fieldState: { error: fieldError } }) => {
            const error = fieldError as unknown as FieldError
            const errorMessage = error?.message || error?.value?.message || ''

            return (
              <AccountDropdown
                value={field.value}
                onChange={field.onChange}
                errorMessage={errorMessage}
                label="GL Account"
                sx={{ minWidth: 'auto' }}
              />
            )
          }}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <OfficeAutoComplete name={`${basePath}.office`} label="Profit Center" sx={{ pt: 0 }} autoSelectOnTab />
      </Grid>

      <Grid item xs={12} md={showRemove ? 2.8 : 3}>
        <CustomController
          controllerName={`${basePath}.debitAmount`}
          controllerLabel="Debit"
          fieldType="number"
          sx={{ my: 0 }}
          dollarAdornment
          isDisabled={hasCreditValue}
          onBlur={() => {
            if (Number(debitAmount) === 0) setValue(`${basePath}.debitAmount`, '')
          }}
        />
      </Grid>

      <Grid item xs={12} md={showRemove ? 2.8 : 3}>
        <CustomController
          controllerName={`${basePath}.creditAmount`}
          controllerLabel="Credit"
          fieldType="number"
          sx={{ my: 0 }}
          dollarAdornment
          isDisabled={hasDebitValue}
          onBlur={() => {
            if (Number(creditAmount) === 0) setValue(`${basePath}.creditAmount`, '')
          }}
        />
      </Grid>

      {showRemove && (
        <Grid item xs={12} md={0.4} display="flex" justifyContent="center" alignItems="center">
          <IconButton onClick={onRemove}>
            <DeleteOutline fontSize="small" sx={{ color: 'primary.main' }} />
          </IconButton>
        </Grid>
      )}
    </Grid>
  )
}
